import type { ApolloError } from 'apollo-client';

/**
 * Standardized error attributes for analytics events.
 * Used to ensure consistent error reporting across all query/mutation failures.
 */
export type ApolloErrorInterop = {
	/** The error message */
	message: string | undefined;
	/** The error name/type */
	name: string | undefined;
	/** The error stack trace */
	stack: string | undefined;
	/** The underlying cause of the error */
	cause: unknown;
	/** Number of GraphQL errors in the response */
	graphQLErrorsCount?: number;
	/** HTTP network status code if available */
	networkStatusCode?: number;
	/** Array of error codes from GraphQL errors */
	errorCodes?: string[];
};

/**
 * Converts an Apollo error (or any error) into a standardized attribute shape
 * for analytics events. This ensures consistent error reporting across all
 * query and mutation failures in the Share and Restrict Dialog.
 *
 * @param error - The error to convert (Apollo error, standard Error, or unknown)
 * @returns Standardized error attributes for analytics
 */
export function toApolloErrorAttributes(error: unknown): ApolloErrorInterop {
	if (!error) {
		return {
			message: undefined,
			name: undefined,
			stack: undefined,
			cause: undefined,
		};
	}

	// Handle standard Error objects
	if (error instanceof Error) {
		const baseAttributes: ApolloErrorInterop = {
			message: error.message,
			name: error.name,
			stack: error.stack,
			cause: (error as Error & { cause?: unknown }).cause,
		};

		// Check if it's an Apollo error with additional properties
		const apolloError = error as ApolloError;
		if (apolloError.graphQLErrors) {
			baseAttributes.graphQLErrorsCount = apolloError.graphQLErrors.length;
			const errorCodes: string[] = [];
			for (const gqlError of apolloError.graphQLErrors) {
				const code = gqlError.extensions?.code;
				if (code && typeof code === 'string') {
					errorCodes.push(code);
				}
			}
			baseAttributes.errorCodes = errorCodes;
		}

		if (apolloError.networkError) {
			const networkError = apolloError.networkError as { statusCode?: number };
			baseAttributes.networkStatusCode = networkError.statusCode;
		}

		return baseAttributes;
	}

	// Handle non-Error objects (fallback)
	return {
		message: String(error),
		name: 'UnknownError',
		stack: undefined,
		cause: undefined,
	};
}
